// app/javascript/utils/firebase/popupRedirectResolver.ts
//
// Firebase Auth's popup/redirect machinery is a hidden cross-origin iframe:
// https://apis.google.com/js/api.js, its gapi_iframes bundle, and
// https://<authDomain>/__/auth/iframe + iframe.js (~140 KB together). getAuth()
// wires in `browserPopupRedirectResolver`, whose `_shouldInitProactively` getter is
// true on mobile browsers and Safari, so on those browsers every page load
// builds that iframe while Auth initialises, whether or not the visitor ever
// signs in. Desktop Chrome/Firefox already skip it and build it on the first
// signInWithPopup() call.
//
// We want the desktop behaviour everywhere, plus an explicit warm-up when
// sign-in UI opens. The warm-up matters: signInWithPopup() awaits the iframe
// before calling window.open(), and Safari only lets a popup open shortly after
// the tap. That is why the SDK pre-builds it on mobile. Warming up when the
// sign-in modal opens gets the iframe ready before the visitor taps
// "Continue with Google/Apple".
//
// `_shouldInitProactively` and `_initialize` are SDK internals. If a future SDK
// renames them, the subclass override does nothing and the SDK goes back to
// building the iframe proactively: slower, but sign-in keeps working. The Jest
// spec checks that both internals still exist so an upgrade that renames them
// shows up as a test failure.

import type { Auth, PopupRedirectResolver } from 'firebase/auth';

type ResolverClass = new (...args: any[]) => object;

/**
 * A subclass of the SDK's resolver class (initializeAuth expects the class, not
 * an instance) that never initialises itself during Auth start-up.
 */
export function withoutProactiveInit<T extends ResolverClass>(Base: T): T {
  return class LazyPopupRedirectResolver extends Base {
    get _shouldInitProactively(): boolean {
      return false;
    }
  };
}

/** The browser resolver, minus its proactive iframe build. */
export function lazyBrowserPopupRedirectResolver(
  browserResolver: PopupRedirectResolver,
): PopupRedirectResolver {
  return withoutProactiveInit(
    browserResolver as unknown as ResolverClass,
  ) as unknown as PopupRedirectResolver;
}

/**
 * Start building the popup/redirect iframe for `auth` now, so a popup sign-in
 * that follows opens straight away. Safe to call many times: the SDK caches the
 * build per Auth instance and drops a failed one, so a later call retries.
 * Never rejects.
 */
export function warmUpPopupRedirectResolver(auth: Auth | null | undefined): Promise<void> {
  const resolver = (auth as any)?._popupRedirectResolver;
  if (!resolver || typeof resolver._initialize !== 'function') {
    return Promise.resolve();
  }
  return Promise.resolve()
    .then(() => resolver._initialize(auth))
    .then(
      () => undefined,
      (error: unknown) => {
        console.warn('[FirebaseAuth] Popup sign-in warm-up failed; it will retry on sign-in:', error);
      },
    );
}
